import { useState } from "react";
import { openWhatsApp } from "../lib/whatsapp";
import dayjs from "dayjs";
import clsx from "clsx";
import { db, eq } from "../lib/api";
import { invalidate, useQuery } from "../lib/hooks";
import { useRefData } from "../lib/refdata";
import { useSession } from "../lib/session";
import { PageHead } from "../components/Shell";
import { Empty, ErrorBox, Field, Loading, Panel, Seg, Sheet, useToast } from "../components/ui";
import { openPerson } from "../components/PersonDrawer";
import { useRoute } from "../lib/router";
import { IconCake } from "../components/icons";

export type Group = "all" | "team" | "leads" | "clients";
export type Bday = {
  person_id: string; full_name: string; first_name: string; birthday: string; days_away: number; turning: number | null;
  is_team: boolean; is_lead: boolean; is_client: boolean; is_minor: boolean;
  contact_person_id: string; contact_name: string; contact_first_name: string; contact_phone: string | null; contact_email: string | null;
  family_context: string | null;
};

const inGroup = (b: Bday, g: Group) => g === "all" || (g === "team" && b.is_team) || (g === "leads" && b.is_lead) || (g === "clients" && b.is_client);

/** Turn a phone into WhatsApp's international format. Canadian/US 10-digit numbers get a leading 1. */
export function waNumber(phone?: string | null) {
  const d = (phone ?? "").replace(/\D/g, "");
  if (!d) return null;
  if (d.length === 10) return "1" + d;
  return d;
}

export function useBirthdayMessage() {
  const ref = useRefData();
  const cfg = ref.data?.setting("birthdays") ?? {};
  return (b: Bday) => {
    const t: string = b.is_minor ? cfg.message_child ?? "Hi {parent_first_name}! Wishing {child_first_name} a very happy birthday! 🎉"
                                 : cfg.message_adult ?? "Happy birthday, {first_name}! 🎉";
    return t.replaceAll("{first_name}", b.first_name).replaceAll("{child_first_name}", b.first_name)
            .replaceAll("{parent_first_name}", b.contact_first_name ?? "");
  };
}

/** One-click wish: puts the message on the clipboard, opens WhatsApp to the right person, logs the touch. */
export function WishButton({ b, small }: { b: Bday; small?: boolean }) {
  const msg = useBirthdayMessage();
  const toast = useToast();
  const { can } = useSession();
  async function go() {
    const text = msg(b);
    try { await navigator.clipboard.writeText(text); } catch { /* clipboard may be blocked; WhatsApp still gets the text */ }
    const n = waNumber(b.contact_phone);
    if (n) openWhatsApp(n, text);
    else if (b.contact_email) window.location.href = `mailto:${b.contact_email}?subject=${encodeURIComponent("Happy birthday!")}&body=${encodeURIComponent(text)}`;
    if (can("activities", "edit")) {
      await db.insert("activities", { person_id: b.contact_person_id, area: b.is_team ? "team" : "other", activity_type: "Birthday or special-day wish",
        channel: n ? "WhatsApp" : "Email", what_happened: b.is_minor ? `Wished ${b.full_name} a happy birthday (via ${b.contact_name})` : "Birthday wish sent" }).catch(() => undefined);
      invalidate("today"); invalidate("birthdays");
    }
    toast((n ? "Message copied and WhatsApp opened" : b.contact_email ? "Email opened with the message" : "Message copied (no phone or email on file)") + (can("activities", "edit") ? " · logged as a touch" : ""));
  }
  return (
    <button className={clsx("btn", small ? "small" : "", "wa")} onClick={go} title={b.is_minor ? `Message ${b.contact_name} (parent)` : `Message ${b.full_name}`}>
      <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Zm0 18.2a8.2 8.2 0 0 1-4.2-1.1l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2Zm4.5-6.1c-.2-.1-1.5-.7-1.7-.8s-.4-.1-.6.1-.7.8-.8 1-.3.2-.5.1a6.7 6.7 0 0 1-3.3-2.9c-.3-.4.3-.4.7-1.3a.5.5 0 0 0 0-.5l-.8-1.8c-.2-.5-.4-.4-.6-.4h-.5a1 1 0 0 0-.7.3 3 3 0 0 0-.9 2.2 5.2 5.2 0 0 0 1.1 2.7 11.8 11.8 0 0 0 4.5 4c1.7.7 2.4.8 3.2.7a2.8 2.8 0 0 0 1.8-1.3 2.3 2.3 0 0 0 .2-1.3c-.1-.1-.3-.2-.5-.3Z" /></svg>
      {b.is_minor ? `Wish via ${b.contact_first_name}` : "WhatsApp wish"}
    </button>
  );
}

export function BdayRow({ b }: { b: Bday }) {
  const when = b.days_away === 0 ? "Today" : b.days_away === 1 ? "Tomorrow" : dayjs(b.birthday).format("ddd, MMM D");
  return (
    <li className={clsx("bday", b.days_away === 0 && "today")}>
      <div className="bd-date"><span>{dayjs(b.birthday).format("MMM")}</span><b>{dayjs(b.birthday).format("D")}</b></div>
      <div className="bd-main">
        <button className="linkish" onClick={() => openPerson(b.person_id)}><strong>{b.full_name}</strong></button>
        <div className="small muted">
          {when}{b.days_away > 1 ? ` · in ${b.days_away} days` : ""}{b.turning != null ? ` · turning ${b.turning}` : ""}
          {b.is_minor && <> · child of <button className="linkish small" onClick={() => openPerson(b.contact_person_id)}>{b.contact_name}</button></>}
        </div>
        <div className="badges" style={{ marginTop: 4 }}>
          {b.is_team && <span className="pill team">Team</span>}
          {b.is_lead && <span className="pill leads">Lead</span>}
          {b.is_client && <span className="pill clients">Client</span>}
          {b.is_minor && <span className="pill special">Child</span>}
        </div>
      </div>
      <div className="bd-act"><WishButton b={b} /></div>
    </li>
  );
}

export function useBirthdays(days?: number) {
  return useQuery(`birthdays:${days ?? "default"}`, () => db.rpc<Bday[]>("birthdays", days == null ? {} : { p_days: days }));
}

/** Compact panel for dashboards: today + this week. */
export function BirthdaysPanel({ group, to }: { group: Group; to: string }) {
  const q = useBirthdays(7);
  const list = (q.data ?? []).filter((b) => inGroup(b, group));
  const today = list.filter((b) => b.days_away === 0);
  return (
    <Panel title="Birthdays" hint={`${today.length} today · ${list.length} in the next 7 days`}
      actions={<a className="btn small ghost" href={to}>See all</a>}>
      {q.error ? <div className="panel-body"><ErrorBox error={q.error} /></div> : !q.data ? <Loading rows={2} /> :
        list.length === 0 ? <Empty title="No birthdays this week" /> :
        <ul className="bday-list">{list.slice(0, 6).map((b) => <BdayRow key={b.person_id} b={b} />)}</ul>}
    </Panel>
  );
}

export default function Birthdays({ group }: { group: Group }) {
  const ref = useRefData();
  const { can } = useSession();
  const toast = useToast();
  const ahead: number = ref.data?.setting("birthdays")?.days_ahead ?? 14;
  const [range, setRange] = useState<"today" | "week" | "ahead">("week");
  const { query } = useRoute();
  const [g, setG] = useState<Group>((["all", "team", "leads", "clients"].includes(query.get("g") ?? "") ? query.get("g") : group) as Group);
  const [editing, setEditing] = useState(false);
  const days = range === "today" ? 0 : range === "week" ? 7 : ahead;
  const q = useBirthdays(days);
  const list = (q.data ?? []).filter((b) => inGroup(b, g));
  const label = g === "all" ? "everyone" : g === "team" ? "your team" : g === "leads" ? "your leads" : "your clients";

  return (
    <>
      <PageHead title="Birthdays" sub={`Never miss a birthday for ${label}. One click copies a warm message and opens WhatsApp to them, or to the parent when it's a child's birthday.`}
        actions={can("settings", "edit") && <button className="btn" onClick={() => setEditing(true)}>Messages & days ahead</button>} />
      <div className="filters">
        <Seg value={range} onChange={setRange} options={[{ value: "today", label: "Today" }, { value: "week", label: "This week" }, { value: "ahead", label: `Next ${ahead} days` }]} />
        <Seg value={g} onChange={setG} options={[{ value: "all", label: "Everyone" }, { value: "team", label: "Team" }, { value: "leads", label: "Leads" }, { value: "clients", label: "Clients" }]} />
        <span className="showing"><IconCake size={15} /> {list.length} {list.length === 1 ? "birthday" : "birthdays"}</span>
      </div>
      <ErrorBox error={q.error} />
      <Panel>
        {!q.data ? <Loading rows={5} /> : list.length === 0 ? (
          <Empty title={range === "today" ? "No birthdays today" : "No birthdays in this window"}>Birthdays come from each person's date of birth (or birthday month and day).</Empty>
        ) : <ul className="bday-list">{list.map((b) => <BdayRow key={b.person_id} b={b} />)}</ul>}
      </Panel>
      {editing && <BirthdaySettings onClose={() => setEditing(false)} onSaved={() => { invalidate("ref:"); invalidate("birthdays"); toast("Birthday settings saved"); }} />}
    </>
  );
}

function BirthdaySettings({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const ref = useRefData();
  const cur = ref.data?.setting("birthdays") ?? {};
  const [f, setF] = useState({ days_ahead: cur.days_ahead ?? 14, message_adult: cur.message_adult ?? "", message_child: cur.message_child ?? "" });
  const [err, setErr] = useState<Error | null>(null);
  async function save() {
    try {
      await db.update("settings", { key: eq("birthdays") }, { value: { ...cur, ...f, days_ahead: Number(f.days_ahead) || 14 } });
      onSaved(); onClose();
    } catch (e) { setErr(e as Error); }
  }
  return (
    <Sheet title="Birthday settings" onClose={onClose} footer={<><button className="btn" onClick={onClose}>Cancel</button><button className="btn primary" onClick={save}>Save</button></>}>
      <ErrorBox error={err} />
      <Field label="Show birthdays this many days ahead (dashboards and the 'Next N days' view)">
        <input className="input" type="number" min={1} max={90} value={f.days_ahead} onChange={(e) => setF({ ...f, days_ahead: Number(e.target.value) })} style={{ maxWidth: 120 }} />
      </Field>
      <Field label="Message to an adult · use {first_name}">
        <textarea className="textarea" rows={4} value={f.message_adult} onChange={(e) => setF({ ...f, message_adult: e.target.value })} />
      </Field>
      <Field label="Message to a parent for a child's birthday · use {parent_first_name} and {child_first_name}">
        <textarea className="textarea" rows={4} value={f.message_child} onChange={(e) => setF({ ...f, message_child: e.target.value })} />
      </Field>
    </Sheet>
  );
}
